import { useQuery } from "@apollo/client";
import Avatar from "components/core/Avatar";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import type { ICompanyIssuerFieldsFragment } from "lib/graphql/fragments/__generated__/CompanyIssuerFields.generated";
import RelatedPeersQuery from "lib/graphql/queries/RelatedPeersQuery";
import type {
  IRelatedPeersQuery,
  IRelatedPeersQueryVariables,
} from "lib/graphql/queries/__generated__/RelatedPeersQuery.generated";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { useRef } from "react";
import { useIntl } from "react-intl";
import type { IPlatformVertical } from "types/graphqlTypes";

export type SelectedCompanyIssuer = Omit<ICompanyIssuerFieldsFragment, "__typename">;

interface RelatedPeersComboOfferButtonProps {
  limit?: number;
  vertical: IPlatformVertical;
  tickerSymbol: string;
  selectedIssuers?: SelectedCompanyIssuer[];
  onClick: (combo: ICompanyIssuerFieldsFragment[]) => void;
}
export default function RelatedPeersComboOfferButton({
  limit = 5,
  vertical,
  tickerSymbol,
  selectedIssuers,
  onClick,
}: RelatedPeersComboOfferButtonProps) {
  const intl = useIntl();
  const acceptedOfferRef = useRef<boolean>(false);
  const { data, loading } = useQuery<IRelatedPeersQuery, IRelatedPeersQueryVariables>(RelatedPeersQuery, {
    skip: tickerSymbol === "",
    variables: {
      tickerSymbol,
      filterByVertical: vertical || null,
    },
  });

  const peers =
    [
      data?.relatedPeers?.competitors || [],
      data?.relatedPeers?.comparables || [],
      data?.relatedPeers?.industryPeers || [],
    ].find((group) => group.length > 0) || [];
  const combo = peers.map((competitor) => competitor.issuer).filter(notUndefinedOrNull) || [];

  if (loading) {
    return (
      <button
        disabled={true}
        type="button"
        aria-label="Loading..."
        className={classNames(
          "inline-flex h-8 w-24 rounded-lg border border-transparent bg-slate-200",
          "animate-pulse ring-1 ring-slate-200 ring-offset-2 ring-offset-white",
          "dark:bg-slate-600 dark:ring-slate-600 dark:ring-offset-slate-600",
        )}
      />
    );
  }

  // Can't apply due to limit
  if ((selectedIssuers || []).length + combo.length > limit) {
    return null;
  }

  const taken = combo.some((candidate) => {
    if (selectedIssuers) {
      return selectedIssuers.some((selected) => selected._id === candidate._id);
    }
    return false;
  });
  if (acceptedOfferRef.current || combo.length === 0 || taken) {
    return null;
  }

  return (
    <SimpleTextTooltip
      content={
        combo.length === 1
          ? intl.formatMessage(
              {
                id: "vpPCcd",
                defaultMessage: "Comparar com: {name}",
                description: "Tooltip text to show when hovering over the combo offer button",
              },
              {
                name: combo[0].name,
              },
            )
          : intl.formatMessage({
              id: "v/sqjz",
              defaultMessage: "Aplicar Combo",
              description: "Tooltip text to show when hovering over the combo offer button",
            })
      }
    >
      <button
        type="button"
        className={classNames(
          "grid w-full grid-flow-col rounded-xl border border-transparent bg-slate-50 p-1",
          "ring-1 ring-slate-200 ring-offset-2 ring-offset-white focus-visible:outline-none",
          "sm:flex sm:w-auto sm:max-w-xs sm:justify-normal sm:space-x-2",
          "dark:bg-slate-950 dark:ring-slate-950 dark:ring-offset-slate-950",
          "hover:opacity-80",
        )}
        onClick={() => {
          acceptedOfferRef.current = true;
          onClick(combo);
        }}
      >
        {combo.map((issuer) => (
          <Avatar
            key={issuer.tickerSymbol}
            src={issuer.niceLogo?.url || null}
            hashValue={issuer._id}
            alt={issuer.name}
            size={28}
            squircle={true}
            className="md:inline-flex"
          />
        ))}
      </button>
    </SimpleTextTooltip>
  );
}
